// =============================================================================
// US-PLATFORM-07 -- Theme mode (light / dark)
// =============================================================================
//
// Module-level Solid signal for the active visual theme. Same pattern
// as state/a11yPreferences.ts and state/mobilePreview.ts. The current
// account's choice is stored locally per account and is loaded whenever the
// active account changes, so one family member's visual preference cannot
// change another family member's view on a shared device.
//
// The signal drives a `data-theme="light|dark"` attribute on the
// App.tsx root wrapper. CSS in styles/app.css keys off it:
//
//     [data-theme='dark'] {
//       --bg-1: ...;
//       --ink-1: ...;
//       /* etc. */
//     }
//
// Per the spec, dark mode interacts cleanly with the existing a11y
// toggles:
//   * High-contrast wins over dark -- high-contrast forces pure black
//     text on white surfaces (or vice versa) regardless of the theme
//     setting, since the user has explicitly asked for maximum
//     contrast and that decision overrides aesthetics.
//   * Large-font is orthogonal -- it just bumps the root font size,
//     no colour interaction.
//
// Default: light. Dark mode is opt-in via Settings -> Display.

import { createSignal } from 'solid-js';
import { appearanceStorageKey, registerAppearanceLoader } from './accountAppearance';

export type ThemeMode = 'light' | 'dark';

const [themeMode, setThemeMode] = createSignal<ThemeMode>('light');

function loadThemeForActiveAccount(): void {
  try {
    const key = appearanceStorageKey('theme');
    setThemeMode(key !== null && localStorage.getItem(key) === 'dark' ? 'dark' : 'light');
  } catch {
    setThemeMode('light');
  }
}

registerAppearanceLoader(loadThemeForActiveAccount);

export function setAccountThemeMode(value: ThemeMode): void {
  setThemeMode(value);
  try {
    const key = appearanceStorageKey('theme');
    if (key !== null) localStorage.setItem(key, value);
  } catch {
    /* local storage is unavailable; retain the current session preference. */
  }
}

export { themeMode, setAccountThemeMode as setThemeMode };
